import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';
import {
  QueryTransactionDTO,
  QueryTransactionResponse,
  TransactionDetails
} from 'app/modules/payments/dtos/payment.model';
import { ItemsResponse } from '@dtos/items-response/items-response';
import { LoaderService } from '@services/loader.service';
import { AuthorizePreviousResponse, ClientDTO, ProfileDTO } from 'models/DTOs';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  paymentSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  http = inject(HttpClient);
  loaderService: LoaderService = inject(LoaderService);
  lastTransaction: string;

  getTransactions(item?: QueryTransactionDTO): Observable<ItemsResponse<TransactionDetails>> {
    const query = {
      ...item,
      startDate: this.getLastThreeMonthsDate()
    };

    return this.http.post<QueryTransactionResponse>(`${environment.baseApiUrl}/plugnpay/query-transaction`, query).pipe(
      map(value => {
        return {
          items: value.transactionDetailsList,
          paginator: {
            pageIndex: 0,
            pageSize: 10,
            totalItems: value.transactionDetailsList.length
          }
        };
      })
    );
  }

  getClientLastOrderId(clientID: string) {
    const params = new HttpParams();
    params.append('policyId', clientID);

    return this.http
      .get<string>(`${environment.baseApiUrl}/plugnpay/${clientID}/order-id`, {
        params: params
      })
      .pipe(tap(() => this.loaderService.toggleLoader(false)));
  }

  setClientOrderId(clientID: string, orderID: string): Observable<ClientDTO> {
    return this.http.get<ClientDTO>(`${environment.baseApiUrl}/clients/${clientID}`).pipe(
      switchMap(client => {
        const updatedClient: ClientDTO = { ...client, externalPaymentID: orderID };
        return this.http.put<ClientDTO>(`${environment.baseApiUrl}/clients/${clientID}`, updatedClient);
      })
    );
  }

  payItem(clientID: string, amount: number): Observable<AuthorizePreviousResponse> {
    return this.http
      .post<AuthorizePreviousResponse>(`${environment.baseApiUrl}/plugnpay/${clientID}/pay-one`, amount)
      .pipe(catchError(this.handleError));
  }

  handlePaymentFormSubmission(
    paymentForm: any,
    policyType: string,
    itemDescription: string,
    amount: number,
    monthsPlan: number,
    createdPolicyId: string,
    createdPolicyNumber: string = null,
    profile: ProfileDTO = null
  ) {
    const totalPrice = (monthsPlan === 12 ? amount : amount * monthsPlan).toFixed(2);
    const inputs = paymentForm.nativeElement.querySelectorAll('input');
    inputs.forEach((input: HTMLInputElement) => {
      switch (input.name) {
        case 'pt_order_classifier':
          input.value = createdPolicyNumber.toString();
          break;
        case 'pt_account_code_1':
          input.value = createdPolicyId.toString();
          break;

        case 'pt_item_identifier_1':
          input.value = policyType;
          break;
        case 'pt_item_description_1':
          input.value = !!createdPolicyNumber
            ? `${itemDescription}: ${createdPolicyNumber} (${totalPrice}$ total)`
            : `${itemDescription} (${totalPrice}$ total)`;
          break;
        case 'pt_item_cost_1':
          input.value = monthsPlan === 12 ? (amount % 1000).toFixed(2) : amount.toFixed(2); //TMP: TEMPORARY LIMIT TO PAYMENT AMOUNT FOR TESTING!
          break;

        //Recurring form exclusive inputs:
        case 'pr_balance':
          input.value = (amount * (monthsPlan - 1)).toFixed(2);
          break;
        case 'pr_recurring_amount':
          input.value = amount.toFixed(2);
          break;

        case 'pm_username':
          input.value = profile.email;
          break;
        case 'pb_customer_password_confirmation':
          input.value = profile.firstName + '_' + profile.lastName;
          break;

        //Initial recurring form exclusive inputs:
        case 'pt_customer_username':
          input.value = profile.email;
          break;
        case 'pb_customer_password':
          input.value = profile.firstName + '_' + profile.lastName;
          break;
      }
    });

    if (createdPolicyNumber) localStorage.setItem('createdPolicyNumber', createdPolicyNumber);

    paymentForm.nativeElement.submit();
  }

  private handleError(error: any) {
    const errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    if (error.status === 404) console.log(errorMessage);
    return throwError(() => {
      return errorMessage;
    });
  }

  private getLastThreeMonthsDate() {
    let date = new Date();
    const newDate = date.setMonth(date.getMonth() - 3);
    date = new Date(newDate);
    const dstr = `${date.getFullYear()}${date.getMonth() + 1}${date.getDay()}`;
    return dstr;
  }
}
